"use client";

import { Button } from "@mui/material";
import Link from "next/link";

/**
 * Reusable CTA Button Component
 * 
 * Variants:
 * - primary: Brand green solid button (main CTAs)
 * - secondary: Outline button (secondary actions on light backgrounds)
 * - secondaryLight: Outline button for dark backgrounds (e.g., video hero)
 */
export default function CTAButton({
  children,
  href,
  variant = "primary",
  size = "large",
  fullWidth = false,
  onDark = false, // Use true when button is on a dark background
  ...props
}) {
  const styles = {
    // Primary: Brand green solid button with gold text
    primary: {
      backgroundColor: "primary.main",
      color: "secondary.main",
      "&:hover": {
        backgroundColor: "primary.light",
      },
    },
    // Secondary: Outline button for LIGHT backgrounds
    secondary: {
      backgroundColor: "transparent",
      color: "text.primary",
      border: "2px solid",
      borderColor: "primary.main",
      boxSizing: "border-box",
      "&:hover": {
        backgroundColor: "background.accent",
      },
    },
  };

  // Override for dark backgrounds (onDark prop)
  const darkOverrides = {
    primary: {
      // Primary stays the same on dark
    },
    secondary: {
      color: "text.inverse",
      borderColor: "text.inverse",
      "&:hover": {
        backgroundColor: "background.accent",
        borderColor: "primary.main",
        color: "text.primary",
      },
    },
  };

  const sizeStyles = {
    large: {
      px: 4,
      py: 2,           // Increased from 1.5 for touch target
      fontSize: "1.1rem",
      minHeight: "48px", // WCAG touch target minimum
    },
    medium: {
      px: 3,
      py: 1.5,         // Increased from 1 for touch target
      fontSize: "1rem",
      minHeight: "44px",
    },
    small: {
      px: 2.5,         // Increased from 2 for touch target
      py: 1,           // Increased from 0.75 for touch target
      fontSize: "0.875rem",
      minHeight: "40px",
    },
  };

  // Merge base styles with dark overrides if needed
  const buttonStyles = onDark
    ? { ...styles[variant], ...darkOverrides[variant] }
    : styles[variant];

  const commonSx = {
    ...buttonStyles,
    ...sizeStyles[size],
    borderRadius: "8px",
    fontWeight: 600,
    textTransform: "none",
    width: fullWidth ? "100%" : "auto",
  };

  // Use MUI's component prop to render as Link directly
  // This avoids nesting <button> inside <a>, which causes touch target issues
  if (href) {
    return (
      <Button
        component={Link}
        href={href}
        sx={commonSx}
        {...props}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button sx={commonSx} {...props}>
      {children}
    </Button>
  );
}
